import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Drawer,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CustomButton from "../CustomButton/CustomButton";
import { smallFieldStyles, styles } from "./DrawerMenuStyles";

const DrawerMenu = ({ drawerFilters, onApply, onClear, initialValues }) => {
  const [isOpen, setIsOpen] = useState(false);

  const getInitialState = () =>
    drawerFilters.reduce((acc, filter) => {
      if (filter.type === "multi-select")
        acc[filter.filterKey] = initialValues?.[filter.filterKey] || [];
      else if (filter.type === "daterange") {
        acc[`${filter.filterKey}_from`] =
          initialValues?.[`${filter.filterKey}_from`] || null;
        acc[`${filter.filterKey}_to`] =
          initialValues?.[`${filter.filterKey}_to`] || null;
      } else acc[filter.filterKey] = initialValues?.[filter.filterKey] ?? "";
      return acc;
    }, {});

  const [localValues, setLocalValues] = useState(getInitialState);
  useEffect(() => {
    if (isOpen) {
      setLocalValues(getInitialState());
    }
  }, [isOpen]);
  const handleChange = (key, value) => {
    setLocalValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    onApply(localValues);
    setIsOpen(false);
  };

  const handleClear = () => {
    setLocalValues(getInitialState());
    onClear?.();
    setIsOpen(false);
  };

  return (
    <>
      <CustomButton
        variant="outlined"
        startIcon={<FilterAltIcon />}
        onClick={() => setIsOpen(true)}
        size="small"
        sx={styles.filterButton}
      >
        Filters
      </CustomButton>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Drawer anchor="right" open={isOpen} onClose={() => setIsOpen(false)}>
          <Box sx={styles.drawerContainer}>
            <Typography variant="h6" sx={styles.drawerTitle}>
              Filters
            </Typography>

            <Stack spacing={3}>
              {drawerFilters.map((filter) => {
                if (filter.type === "multi-select") {
                  return (
                    <Box key={filter.filterKey} sx={styles.filterSection}>
                      <Typography variant="body2" sx={styles.filterLabel}>
                        {filter.label}
                      </Typography>
                      <FormControl fullWidth size="small" sx={smallFieldStyles}>
                        <InputLabel>
                          {filter.placeholder || `All ${filter.label}`}
                        </InputLabel>
                        <Select
                          multiple
                          value={localValues[filter.filterKey] || []}
                          onChange={(e) =>
                            handleChange(filter.filterKey, e.target.value)
                          }
                          sx={{ fontSize: "0.875rem" }}
                          input={
                            <OutlinedInput
                              label={
                                filter.placeholder || `All ${filter.label}`
                              }
                            />
                          }
                          renderValue={(selected) =>
                            filter.content
                              ?.filter((c) => selected.includes(c.value))
                              .map((c) => c.label)
                              .join(", ") || selected.join(", ")
                          }
                        >
                          {filter.content?.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Checkbox
                                checked={localValues[
                                  filter.filterKey
                                ]?.includes(option.value)}
                              />
                              <ListItemText primary={option.label} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  );
                }

                if (filter.type === "radio") {
                  return (
                    <Box key={filter.filterKey} sx={styles.filterSection}>
                      <Typography variant="body2" sx={styles.radioFilterLabel}>
                        {filter.label}
                      </Typography>
                      <RadioGroup
                        value={localValues[filter.filterKey] ?? ""}
                        onChange={(e) =>
                          handleChange(filter.filterKey, e.target.value)
                        }
                      >
                        {filter.content?.map((option) => (
                          <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={<Radio size="small" />}
                            label={option.label}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: "0.875rem",
                              },
                            }}
                          />
                        ))}
                      </RadioGroup>
                    </Box>
                  );
                }

                if (filter.type === "daterange") {
                  return (
                    <Box key={filter.filterKey} sx={styles.filterSection}>
                      <Typography variant="body2" sx={styles.filterLabel}>
                        {filter.label}
                      </Typography>
                      <Stack spacing={2}>
                        <DatePicker
                          label="From"
                          value={localValues[`${filter.filterKey}_from`]}
                          onChange={(val) =>
                            handleChange(`${filter.filterKey}_from`, val)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              fullWidth
                              sx={{
                                "& .MuiInputLabel-root": {
                                  fontSize: "0.875rem",
                                },
                                "& .MuiInputBase-input": {
                                  fontSize: "0.875rem",
                                },
                              }}
                            />
                          )}
                        />
                        <DatePicker
                          label="To"
                          value={localValues[`${filter.filterKey}_to`]}
                          onChange={(val) =>
                            handleChange(`${filter.filterKey}_to`, val)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              fullWidth
                              sx={{
                                "& .MuiInputLabel-root": {
                                  fontSize: "0.875rem",
                                },
                                "& .MuiInputBase-input": {
                                  fontSize: "0.875rem",
                                },
                              }}
                            />
                          )}
                        />
                      </Stack>
                    </Box>
                  );
                }

                return null;
              })}

              <Stack direction="row" spacing={2} sx={styles.actionRow}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleClear}
                  sx={{ textTransform: "capitalize" }}
                >
                  Clear All
                </Button>
                <CustomButton classes={styles.doneButton} onClick={handleApply}>
                  Done
                </CustomButton>
              </Stack>
            </Stack>
          </Box>
        </Drawer>
      </LocalizationProvider>
    </>
  );
};

DrawerMenu.propTypes = {
  drawerFilters: PropTypes.arrayOf(
    PropTypes.shape({
      filterKey: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.oneOf(["multi-select", "radio", "daterange"]).isRequired,
      placeholder: PropTypes.string,
      content: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.any,
        }),
      ),
    }),
  ).isRequired,
  onApply: PropTypes.func.isRequired,
  onClear: PropTypes.func,
};

export default DrawerMenu;
